/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { isArray } from 'lodash';
import {
    Box,
    Typography,
    Chip,
    alpha,
    TextField,
    InputAdornment,
    CircularProgress,
    Avatar,
    Tooltip,
    IconButton,
    Popover,
    List,
    ListItemButton,
    ListItemText,
    Checkbox,
    Divider,
    Badge,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Iconify } from 'src/components';
import { useSelector, dispatch as reduxDispatch } from 'src/redux/store';
import {
    getTablePreferences,
    handleTablePreferences,
    handleTablePreferencesEdit,
    setTablePreferenceLoading,
} from 'src/redux/slices/tablePreferences';
import { getCurrentTableData } from 'src/utils/tablePreferences';
import useGetCurrentUserId from 'src/hooks/useGetCurrentUserId';
import useReportExportFilename from 'src/hooks/useReportExportFilename';
import LeadListPopup from './LeadListPopup';

const TABLE_NAME = 'TCO Performance Tracker';

// All available columns (field names)
const ALL_COLUMNS = [
    { field: 'name', label: 'Team Member' },
    { field: 'patientsContacted', label: 'Patients' },
    { field: 'leadsContacted', label: 'Contacted' },
    { field: 'contactMethods', label: 'Channels' },
    { field: 'consultationsBooked', label: 'Booked In' },
    { field: 'bookingRate', label: 'Booking %' },
    { field: 'consultationsAttended', label: 'Attended' },
    { field: 'showUpRate', label: 'Show-up %' },
    { field: 'treatmentStarted', label: 'Converted' },
    { field: 'revenue', label: 'Revenue' },
];

// Column configuration for consistent Chip styling
const COLUMN_CONFIG = {
    patientsContacted: { color: '#3b82f6' },
    leadsContacted: { color: '#f59e0b' },
    consultationsBooked: { color: '#6366f1' },
    consultationsAttended: { color: '#8b5cf6' },
    treatmentStarted: { color: '#10b981' },
    revenue: { color: '#10b981' },
    bookingRate: { good: '#10b981', medium: '#f59e0b', low: '#ef4444' },
    showUpRate: { good: '#10b981', medium: '#f59e0b', low: '#ef4444' },
    conversionRate: { good: '#10b981', medium: '#f59e0b', low: '#ef4444' },
};

const CHANNEL_ICONS = {
    PHONE: 'mdi:phone',
    EMAIL: 'mdi:email',
    WHATSAPP: 'ic:round-whatsapp',
    SMS: 'mdi:message-text',
    VISITED: 'mdi:account',
    ONLINE_CHAT: 'mdi:chat',
    SOCIAL_MEDIA: 'mdi:share-variant',
};

const ROLE_COLORS = {
    TCO: { bg: '#dbeafe', text: '#1d4ed8' },
    USER: { bg: '#fef3c7', text: '#d97706' },
    ADMIN: { bg: '#f3e8ff', text: '#7c3aed' },
    default: { bg: '#f1f5f9', text: '#64748b' },
};

export default function TcoPerformanceTracker({
    title,
    data,
    teamTotals,
    isLoading,
}) {
    const [searchQuery, setSearchQuery] = useState('');
    const [sortModel, setSortModel] = useState([{ field: 'consultationsBooked', sort: 'desc' }]);
    const [columnsAnchor, setColumnsAnchor] = useState(null);
    const [exportAnchor, setExportAnchor] = useState(null);
    const [hiddenColumns, setHiddenColumns] = useState([]);
    const [prefsLoaded, setPrefsLoaded] = useState(false);

    // Popup state for showing lead details when clicking on numbers
    const [popupOpen, setPopupOpen] = useState(false);
    const [popupLeadIds, setPopupLeadIds] = useState([]);
    const [popupTitle, setPopupTitle] = useState('');

    // Get practice ID from Redux
    const currentPractice = useSelector((state) => state.practiceTreatmentList?.practiceId);

    // Table preferences from Redux
    const userId = useGetCurrentUserId();
    const buildExportName = useReportExportFilename();
    const { tablePreferences: tablePrefsRaw, globalTablePreferences } = useSelector((state) => state.tablePreferences);
    const prefResults = tablePrefsRaw?.results || tablePrefsRaw || [];

    // Load user preferences on mount
    useEffect(() => {
        if (userId) {
            reduxDispatch(getTablePreferences({ practiceUserId: userId }));
        }
    }, [userId]);

    // Save preferences to API
    const savePreferences = useCallback((newHidden) => {
        if (!userId) return;
        reduxDispatch(setTablePreferenceLoading(true));
        const tableData = getCurrentTableData(prefResults, TABLE_NAME);
        const globalData = getCurrentTableData(globalTablePreferences?.results || [], TABLE_NAME);
        const currentTableData = isArray(tableData) && tableData.length > 0 ? tableData : globalData;
        const currentPreferences = currentTableData?.[0]?.preferences || {};
        const updatedPrefs = { ...currentPreferences, hiddenColumns: newHidden };
        const payload = {
            body: { table_name: TABLE_NAME, preferences: updatedPrefs },
            practiceUserId: userId,
        };
        if (isArray(tableData) && tableData.length > 0) {
            reduxDispatch(handleTablePreferencesEdit({ ...payload, tableId: tableData[0].id }));
        } else {
            reduxDispatch(handleTablePreferences(payload));
        }
    }, [userId, prefResults, globalTablePreferences]);

    // Load saved preferences
    useEffect(() => {
        if (!prefsLoaded && userId) {
            const tableData = getCurrentTableData(prefResults, TABLE_NAME);
            const globalData = getCurrentTableData(globalTablePreferences?.results || [], TABLE_NAME);
            const currentTableData = isArray(tableData) && tableData.length > 0 ? tableData : globalData;
            const prefs = currentTableData?.[0]?.preferences;
            if (prefs?.hiddenColumns && isArray(prefs.hiddenColumns)) {
                setHiddenColumns(prefs.hiddenColumns);
            }
            setPrefsLoaded(true);
        }
    }, [prefResults, globalTablePreferences, userId]);

    // Column visibility model for DataGrid
    const columnVisibilityModel = useMemo(() => {
        const model = {};
        hiddenColumns.forEach((field) => { model[field] = false; });
        return model;
    }, [hiddenColumns]);

    // Whether filter is active
    const isFilterActive = hiddenColumns.length > 0;

    // Toggle column visibility
    const handleToggleColumn = useCallback((field) => {
        const updated = hiddenColumns.includes(field)
            ? hiddenColumns.filter((f) => f !== field)
            : [...hiddenColumns, field];
        setHiddenColumns(updated);
        savePreferences(updated);
    }, [hiddenColumns, savePreferences]);

    // Handle click on a metric chip to show leads
    const handleMetricClick = (leadIds, metricTitle) => {
        if (leadIds && leadIds.length > 0) {
            setPopupLeadIds(leadIds);
            setPopupTitle(metricTitle);
            setPopupOpen(true);
        }
    };

    const handleClosePopup = () => {
        setPopupOpen(false);
        setPopupLeadIds([]);
        setPopupTitle('');
    };

    const getRoleColor = (role) => ROLE_COLORS[role] || ROLE_COLORS.default;

    const getInitials = (name) => {
        if (!name) return '?';
        return name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    const formatCurrency = (value) => {
        if (value >= 1000000) return `£${(value / 1000000).toFixed(1)}M`;
        if (value >= 1000) return `£${(value / 1000).toFixed(1)}K`;
        return `£${value}`;
    };

    const getRateColor = (rate, type) => {
        const numRate = parseFloat(rate) || 0;
        const config = COLUMN_CONFIG[type];
        if (type === 'bookingRate') {
            if (numRate >= 60) return config.good;
            if (numRate >= 40) return config.medium;
            return config.low;
        }
        if (type === 'showUpRate') {
            if (numRate >= 80) return config.good;
            if (numRate >= 60) return config.medium;
            return config.low;
        }
        // conversionRate
        if (numRate >= 50) return config.good;
        if (numRate >= 30) return config.medium;
        return config.low;
    };

    // Filter data based on search
    const filteredData = useMemo(() => {
        if (!data) return [];
        if (!searchQuery) return data;
        return data.filter((row) =>
            row.name?.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [data, searchQuery]);

    // Export CSV — exports exactly what the user currently sees (filtered view)
    const handleExportCSV = useCallback(() => {
        setExportAnchor(null);
        if (!filteredData || filteredData.length === 0) return;
        const visibleCols = ALL_COLUMNS.filter((c) => !hiddenColumns.includes(c.field));
        const headers = visibleCols.map((c) => c.label);
        const rows = filteredData.map((p) => visibleCols.map((col) => {
            if (col.field === 'contactMethods') {
                const methods = p.contactMethods || {};
                return Object.entries(methods).filter(([, v]) => v > 0).map(([k, v]) => `${k}:${v}`).join('; ') || '-';
            }
            if (col.field === 'revenue') return p.revenue || 0;
            if (col.field === 'bookingRate' || col.field === 'showUpRate') return `${p[col.field] || 0}%`;
            return p[col.field] ?? '-';
        }));
        const csvContent = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = buildExportName('tco-performance', undefined);
        link.click();
        URL.revokeObjectURL(link.href);
    }, [filteredData, hiddenColumns]);

    // Build columns
    const columns = useMemo(() => [
        {
            field: 'name',
            headerName: 'Team Member',
            flex: 1.5,
            minWidth: 180,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pl: 1 }}>
                    <Avatar
                        src={params.row.avatar}
                        sx={{
                            width: 36,
                            height: 36,
                            bgcolor: '#e2e8f0',
                            color: '#64748b',
                            fontWeight: 600,
                            fontSize: '13px',
                        }}
                    >
                        {getInitials(params.value)}
                    </Avatar>
                    <Box>
                        <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>
                            {params.value}
                        </Typography>
                        <Chip
                            label={params.row.role}
                            size="small"
                            sx={{
                                height: 18,
                                fontSize: '10px',
                                fontWeight: 600,
                                bgcolor: getRoleColor(params.row.role).bg,
                                color: getRoleColor(params.row.role).text,
                            }}
                        />
                    </Box>
                </Box>
            ),
        },
        {
            field: 'patientsContacted',
            headerName: 'Patients',
            flex: 1,
            minWidth: 90,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const value = params.value || 0;
                const config = COLUMN_CONFIG.patientsContacted;
                const leadIds = params.row.patientsContactedIds || [];
                const isClickable = leadIds.length > 0;
                return (
                    <Chip
                        label={value}
                        size="small"
                        onClick={isClickable ? () => handleMetricClick(
                            leadIds,
                            `${params.row.name} - Patients Contacted`
                        ) : undefined}
                        sx={{
                            bgcolor: value > 0 ? alpha(config.color, 0.12) : 'transparent',
                            color: value > 0 ? config.color : 'text.disabled',
                            fontWeight: 600,
                            fontSize: '12px',
                            height: 26,
                            minWidth: 40,
                            cursor: isClickable ? 'pointer' : 'default',
                            '&:hover': isClickable ? { bgcolor: alpha(config.color, 0.24) } : {},
                        }}
                    />
                );
            },
        },
        {
            field: 'leadsContacted',
            headerName: 'Contacted',
            flex: 1,
            minWidth: 90,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const value = params.value || 0;
                const config = COLUMN_CONFIG.leadsContacted;
                return (
                    <Chip
                        label={value}
                        size="small"
                        sx={{
                            bgcolor: value > 0 ? alpha(config.color, 0.12) : 'transparent',
                            color: value > 0 ? config.color : 'text.disabled',
                            fontWeight: 600,
                            fontSize: '12px',
                            height: 26,
                            minWidth: 40,
                        }}
                    />
                );
            },
        },
        {
            field: 'contactMethods',
            headerName: 'Channels',
            flex: 1.5,
            minWidth: 140,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const methods = params.value || {};
                const entries = Object.entries(methods).filter(([, count]) => count > 0);
                if (entries.length === 0) {
                    return <Typography sx={{ fontSize: '12px', color: 'text.disabled' }}>—</Typography>;
                }
                return (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                        {entries.map(([channel, count]) => (
                            <Tooltip key={channel} title={`${channel.replace(/_/g, ' ')}: ${count}`} arrow>
                                <Chip
                                    icon={<Iconify icon={CHANNEL_ICONS[channel] || 'mdi:help-circle'} width={14} />}
                                    label={count}
                                    size="small"
                                    sx={{
                                        height: 22,
                                        fontSize: '10px',
                                        fontWeight: 600,
                                        bgcolor: alpha('#64748b', 0.08),
                                        color: '#64748b',
                                        '& .MuiChip-icon': { fontSize: 14, color: '#64748b' },
                                    }}
                                />
                            </Tooltip>
                        ))}
                    </Box>
                );
            },
        },
        {
            field: 'consultationsBooked',
            headerName: 'Booked In',
            flex: 1,
            minWidth: 90,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const value = params.value || 0;
                const config = COLUMN_CONFIG.consultationsBooked;
                const leadIds = params.row.consultationsBookedIds || [];
                const isClickable = leadIds.length > 0;
                return (
                    <Chip
                        label={value}
                        size="small"
                        onClick={isClickable ? () => handleMetricClick(
                            leadIds,
                            `${params.row.name} - Booked In`
                        ) : undefined}
                        sx={{
                            bgcolor: value > 0 ? alpha(config.color, 0.12) : 'transparent',
                            color: value > 0 ? config.color : 'text.disabled',
                            fontWeight: 600,
                            fontSize: '12px',
                            height: 26,
                            minWidth: 40,
                            cursor: isClickable ? 'pointer' : 'default',
                            '&:hover': isClickable ? { bgcolor: alpha(config.color, 0.24) } : {},
                        }}
                    />
                );
            },
        },
        {
            field: 'bookingRate',
            headerName: 'Booking %',
            flex: 1,
            minWidth: 90,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const rate = parseFloat(params.value) || 0;
                const color = getRateColor(rate, 'bookingRate');
                return (
                    <Chip
                        label={`${rate}%`}
                        size="small"
                        sx={{
                            bgcolor: alpha(color, 0.12),
                            color,
                            fontWeight: 600,
                            fontSize: '12px',
                            height: 26,
                            minWidth: 50,
                        }}
                    />
                );
            },
        },
        {
            field: 'consultationsAttended',
            headerName: 'Attended',
            flex: 1,
            minWidth: 90,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const value = params.value || 0;
                const config = COLUMN_CONFIG.consultationsAttended;
                const leadIds = params.row.consultationsAttendedIds || [];
                const isClickable = leadIds.length > 0;
                return (
                    <Chip
                        label={value}
                        size="small"
                        onClick={isClickable ? () => handleMetricClick(
                            leadIds,
                            `${params.row.name} - Attended`
                        ) : undefined}
                        sx={{
                            bgcolor: value > 0 ? alpha(config.color, 0.12) : 'transparent',
                            color: value > 0 ? config.color : 'text.disabled',
                            fontWeight: 600,
                            fontSize: '12px',
                            height: 26,
                            minWidth: 40,
                            cursor: isClickable ? 'pointer' : 'default',
                            '&:hover': isClickable ? { bgcolor: alpha(config.color, 0.24) } : {},
                        }}
                    />
                );
            },
        },
        {
            field: 'showUpRate',
            headerName: 'Show-up %',
            flex: 1,
            minWidth: 90,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const rate = parseFloat(params.value) || 0;
                const color = getRateColor(rate, 'showUpRate');
                return (
                    <Chip
                        label={`${rate}%`}
                        size="small"
                        sx={{
                            bgcolor: alpha(color, 0.12),
                            color,
                            fontWeight: 600,
                            fontSize: '12px',
                            height: 26,
                            minWidth: 50,
                        }}
                    />
                );
            },
        },
        {
            field: 'treatmentStarted',
            headerName: 'Converted',
            flex: 1,
            minWidth: 90,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const value = params.value || 0;
                const config = COLUMN_CONFIG.treatmentStarted;
                const leadIds = params.row.treatmentStartedIds || [];
                const isClickable = leadIds.length > 0;
                return (
                    <Chip
                        label={value}
                        size="small"
                        onClick={isClickable ? () => handleMetricClick(
                            leadIds,
                            `${params.row.name} - Converted`
                        ) : undefined}
                        sx={{
                            bgcolor: value > 0 ? alpha(config.color, 0.12) : 'transparent',
                            color: value > 0 ? config.color : 'text.disabled',
                            fontWeight: 600,
                            fontSize: '12px',
                            height: 26,
                            minWidth: 40,
                            cursor: isClickable ? 'pointer' : 'default',
                            '&:hover': isClickable ? { bgcolor: alpha(config.color, 0.24) } : {},
                        }}
                    />
                );
            },
        },
        {
            field: 'revenue',
            headerName: 'Revenue',
            flex: 1,
            minWidth: 90,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const value = params.value || 0;
                const config = COLUMN_CONFIG.revenue;
                return (
                    <Chip
                        label={formatCurrency(value)}
                        size="small"
                        sx={{
                            bgcolor: value > 0 ? alpha(config.color, 0.12) : 'transparent',
                            color: value > 0 ? config.color : 'text.disabled',
                            fontWeight: 600,
                            fontSize: '12px',
                            height: 26,
                            minWidth: 50,
                        }}
                    />
                );
            },
        },
    ], []);

    if (isLoading) {
        return (
            <Box
                sx={{
                    bgcolor: '#fff',
                    borderRadius: '16px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    p: 4,
                    mb: 3,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 300,
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    if (!data || data.length === 0) {
        return (
            <Box
                sx={{
                    bgcolor: '#fff',
                    borderRadius: '16px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    p: 4,
                    mb: 3,
                    textAlign: 'center',
                }}
            >
                <Box
                    sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '16px',
                        bgcolor: '#f1f5f9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                    }}
                >
                    <Iconify icon="mdi:account-tie-outline" width={32} sx={{ color: '#94a3b8' }} />
                </Box>
                <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#475569', mb: 1 }}>
                    No TCO/User Data Available
                </Typography>
                <Typography sx={{ fontSize: '14px', color: '#94a3b8' }}>
                    Performance data will appear here once TCOs and users have patient interactions recorded.
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                bgcolor: '#fff',
                borderRadius: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                overflow: 'hidden',
                mb: 3,
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    p: 2.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Tooltip title="Booking and conversion performance for treatment coordinators and admin staff." placement="bottom" arrow
                        slotProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '12px', maxWidth: 280 } } }}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                            <Typography sx={{ fontSize: '20px', fontWeight: 700 }}>
                                {title}
                            </Typography>
                            <Iconify icon="mdi:information-outline" width={14} sx={{ color: '#94a3b8' }} />
                        </Box>
                    </Tooltip>
                    {teamTotals && teamTotals.consultationsBooked > 0 && (
                        <Chip
                            label={`${teamTotals.consultationsBooked} Booked`}
                            size="small"
                            sx={{
                                bgcolor: alpha('#6366f1', 0.12),
                                color: '#6366f1',
                                fontWeight: 600,
                                fontSize: '11px',
                            }}
                        />
                    )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <TextField
                        size="small"
                        placeholder="Search team member..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{ width: 200 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Iconify icon="eva:search-outline" width={20} sx={{ color: 'text.disabled' }} />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                    {/* Column visibility */}
                    <Tooltip title="Show/hide columns" arrow>
                        <Badge
                            variant="dot"
                            invisible={!isFilterActive}
                            sx={{
                                '& .MuiBadge-badge': {
                                    bgcolor: '#3b82f6',
                                    width: 8,
                                    height: 8,
                                    minWidth: 8,
                                    top: 4,
                                    right: 4,
                                },
                            }}
                        >
                            <IconButton
                                size="small"
                                onClick={(e) => setColumnsAnchor(e.currentTarget)}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    border: '1px solid',
                                    borderColor: (isFilterActive || columnsAnchor) ? 'primary.main' : '#e2e8f0',
                                    borderRadius: '8px',
                                    bgcolor: columnsAnchor ? alpha('#3b82f6', 0.08) : 'transparent',
                                    '&:hover': { bgcolor: alpha('#3b82f6', 0.08) },
                                }}
                            >
                                <Iconify icon="eva:options-2-outline" width={16} sx={{ color: (isFilterActive || columnsAnchor) ? 'primary.main' : '#64748b' }} />
                            </IconButton>
                        </Badge>
                    </Tooltip>

                    {/* Export */}
                    <Tooltip title="Export data" arrow>
                        <IconButton
                            size="small"
                            onClick={(e) => setExportAnchor(e.currentTarget)}
                            sx={{
                                width: 32,
                                height: 32,
                                border: '1px solid',
                                borderColor: exportAnchor ? 'primary.main' : '#e2e8f0',
                                borderRadius: '8px',
                                bgcolor: exportAnchor ? alpha('#3b82f6', 0.08) : 'transparent',
                                '&:hover': { bgcolor: alpha('#3b82f6', 0.08) },
                            }}
                        >
                            <Iconify icon="eva:download-outline" width={16} sx={{ color: exportAnchor ? 'primary.main' : '#64748b' }} />
                        </IconButton>
                    </Tooltip>

                    {/* Column visibility popover */}
                    <Popover
                        open={Boolean(columnsAnchor)}
                        anchorEl={columnsAnchor}
                        onClose={() => setColumnsAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        slotProps={{ paper: { sx: { mt: 1, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', width: 240, maxHeight: 400 } } }}
                    >
                        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f1f5f9' }}>
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                                Columns
                            </Typography>
                        </Box>
                        <List dense sx={{ py: 0.5 }}>
                            {ALL_COLUMNS.map((col) => {
                                const isVisible = !hiddenColumns.includes(col.field);
                                return (
                                    <ListItemButton
                                        key={col.field}
                                        onClick={() => handleToggleColumn(col.field)}
                                        sx={{ py: 0.25, px: 1.5, opacity: isVisible ? 1 : 0.6 }}
                                    >
                                        <Checkbox
                                            size="small"
                                            checked={isVisible}
                                            sx={{ p: 0, mr: 1 }}
                                        />
                                        <ListItemText
                                            primary={col.label}
                                            primaryTypographyProps={{ fontSize: '13px', color: isVisible ? '#1e293b' : '#94a3b8' }}
                                        />
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    </Popover>

                    {/* Export popover */}
                    <Popover
                        open={Boolean(exportAnchor)}
                        anchorEl={exportAnchor}
                        onClose={() => setExportAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        slotProps={{ paper: { sx: { mt: 1, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', minWidth: 200 } } }}
                    >
                        <List dense sx={{ py: 0.5 }}>
                            <ListItemButton onClick={handleExportCSV} sx={{ py: 1, px: 2 }}>
                                <Iconify icon="mdi:file-delimited-outline" width={18} sx={{ color: '#64748b', mr: 1.5 }} />
                                <ListItemText primary="Export CSV" primaryTypographyProps={{ fontSize: '13px' }} />
                            </ListItemButton>
                        </List>
                    </Popover>
                </Box>
            </Box>

            {/* DataGrid */}
            <Box sx={{ height: 400 }}>
                <DataGridPro
                    rows={filteredData}
                    columns={columns}
                    sortModel={sortModel}
                    onSortModelChange={setSortModel}
                    columnVisibilityModel={columnVisibilityModel}
                    disableRowSelectionOnClick
                    disableColumnMenu={false}
                    pagination
                    pageSizeOptions={[5, 10, 25]}
                    rowHeight={64}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 10 } },
                    }}
                    getRowId={(row) => row.id ?? row.name ?? `row-${Math.random()}`}
                    sx={{
                        border: 'none',
                        '& .MuiDataGrid-columnHeaders': {
                            bgcolor: '#f8fafc',
                            borderBottom: '1px solid #e2e8f0',
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            fontWeight: 600,
                            fontSize: '12px',
                            color: '#64748b',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                        },
                        '& .MuiDataGrid-cell': {
                            borderBottom: '1px solid #f1f5f9',
                            py: 1,
                        },
                        '& .MuiDataGrid-row:hover': {
                            bgcolor: '#f8fafc',
                        },
                    }}
                />
            </Box>

            {/* Footer legend */}
            <Box
                sx={{
                    p: 2,
                    bgcolor: '#f8fafc',
                    borderTop: '1px solid #e2e8f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                }}
            >
                <Box sx={{ display: 'flex', gap: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label="TCO" size="small" sx={{ height: 18, fontSize: '10px', bgcolor: ROLE_COLORS.TCO.bg, color: ROLE_COLORS.TCO.text }} />
                        <Typography sx={{ fontSize: '11px', color: '#64748b' }}>Treatment Coordinator</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label="USER" size="small" sx={{ height: 18, fontSize: '10px', bgcolor: ROLE_COLORS.USER.bg, color: ROLE_COLORS.USER.text }} />
                        <Typography sx={{ fontSize: '11px', color: '#64748b' }}>User</Typography>
                    </Box>
                </Box>
                <Typography sx={{ fontSize: '11px', color: '#94a3b8' }}>
                    Click numbers to view leads
                </Typography>
            </Box>

            {/* Lead Popup */}
            <LeadListPopup
                open={popupOpen}
                onClose={handleClosePopup}
                practiceId={currentPractice}
                leadIds={popupLeadIds}
                title={popupTitle}
            />
        </Box>
    );
}

TcoPerformanceTracker.propTypes = {
    title: PropTypes.string,
    data: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            name: PropTypes.string,
            role: PropTypes.string,
            avatar: PropTypes.string,
            patientsContacted: PropTypes.number,
            leadsContacted: PropTypes.number,
            contactMethods: PropTypes.object,
            consultationsBooked: PropTypes.number,
            consultationsAttended: PropTypes.number,
            treatmentStarted: PropTypes.number,
            revenue: PropTypes.number,
            bookingRate: PropTypes.string,
            showUpRate: PropTypes.string,
            conversionRate: PropTypes.string,
        })
    ),
    teamTotals: PropTypes.object,
    isLoading: PropTypes.bool,
};

TcoPerformanceTracker.defaultProps = {
    title: 'TCO & User Performance',
    data: [],
    teamTotals: {},
    isLoading: false,
};
